import React, { useState, useRef, useEffect } from 'react';
import { IconButton, TextareaField } from '@dopomogai/ui';
import { Send, Loader2 } from 'lucide-react';
import { getContrastingTextColor } from '../utils/colorUtils';

interface MessageInputProps {
  disabled?: boolean;          // textarea disabled (assistant thinking/streaming)
  buttonDisabled?: boolean;    // send button disabled
  onSendMessage: (message: string) => void;
  maxLength?: number;
  placeholder?: string;
  primaryColor?: string;

  // Persistence and layout
  conversationKey?: string;    // per-conversation draft key
  resizeKey?: string | number; // external trigger to remeasure height
}

export const MessageInput = ({
  disabled = false,
  buttonDisabled,
  onSendMessage,
  placeholder = 'Type your message...',
  primaryColor,
  conversationKey,
  resizeKey,
  maxLength,
}: MessageInputProps) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const storageKey = conversationKey ? `dopo:chat:draft:${conversationKey}` : null;

  // Restore saved draft when conversation changes
  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (typeof saved === 'string') {
        setText(saved);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
          }
        });
      } else {
        setText('');
      }
    } catch {
      // ignore
    }
  }, [storageKey]);

  // Save draft on change
  useEffect(() => {
    if (!storageKey) return;
    try {
      if (text && text.trim().length > 0) {
        sessionStorage.setItem(storageKey, text);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      // ignore
    }
  }, [text, storageKey]);

  // Auto-resize on text change
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  // Auto-resize on parent/layout width changes
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const parent = el.parentElement || el;
    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(() => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    });
    observerRef.current.observe(parent);
    return () => observerRef.current?.disconnect();
  }, []);

  // External remeasure trigger (e.g., sidebar toggles)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [resizeKey, disabled]);

  const handleSend = () => {
    const msg = text.trim();
    if (!msg) return;
    onSendMessage(msg);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    // Clear saved draft after successful send
    if (storageKey) {
      try { sessionStorage.removeItem(storageKey); } catch {}
    }
    // Keep focus for continued typing
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-3 w-full">
      <div className="flex-1 relative">
        <TextareaField
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            if (maxLength && e.target.value.length > maxLength) return;
            setText(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Assistant is thinking…' : placeholder}
          disabled={disabled}
          rows={1}
          // Prevent manual drag-resize and keep a stable range
          style={{ resize: 'none' }}
          textareaClassName="min-h-[51px] py-3 max-h-[120px] resize-none"
        />
      </div>
      <div className="flex-shrink-0 flex items-end">
        <IconButton
          type="submit"
          icon={disabled ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          ariaLabel={disabled ? 'Sending…' : 'Send message'}
          disabled={Boolean(buttonDisabled) || disabled || !text.trim()}
          variant="primary"
          size="lg"
          style={{
            backgroundColor: primaryColor,
            color: getContrastingTextColor(primaryColor),
          }}
        />
      </div>
    </form>
  );
};
